import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, tokens, Dropdown, Link } from '@hotjar/hotsauce';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { upsell } from '@hotjar/webapp/domains/revenue/upsell';
import { useCurrentSite } from '@hotjar/site';

import type { Site } from '@hotjar/site';
import { useTrafficCoverage } from '@hotjar/webapp/hooks/useTrafficCoverage';
import { noop } from '@hotjar/webapp/lib/function';
import { useHelpHotjarUrl } from '@hotjar/translations';

interface SessionNotRecordedPopoverProps {
  children: ReactElement;
  onShow: () => void;
}

const LEARN_MORE_EXTERNAL_LINK = '/articles/115011624047';

const S = {
  DropdownBody: styled.div`
    padding: ${tokens.spacer.x2};
  `,
  DropdownTitle: styled.p<{ lowTrafficCoverage: boolean }>`
    ${tokens.text('subtitle1')};
    margin-bottom: ${({ lowTrafficCoverage }) =>
      lowTrafficCoverage ? tokens.spacer.x2pt5 : tokens.spacer.x2};
  `,
  DropdownText: styled.p<{ lowTrafficCoverage: boolean }>`
    ${tokens.text('body2')};
    margin-bottom: ${({ lowTrafficCoverage }) =>
      lowTrafficCoverage ? tokens.spacer.x1pt5 : tokens.spacer.x2};
  `,
  DropdownSubText: styled.p`
    ${tokens.text('caption')};
    color: ${tokens.color.text_light};
    margin-bottom: 0;
  `,
  Footer: styled.div`
    display: flex;
    gap: ${tokens.spacer.x1};
    padding: ${tokens.spacer.x2};
    background: ${tokens.ALIAS_COLORS.bg_info};
    border-radius: 0 0 ${tokens.spacer.x0pt5} ${tokens.spacer.x0pt5};
  `,
  FooterLabel: styled.p`
    ${tokens.text('caption')};
    color: ${tokens.color.text_default};
    margin: 0;
  `,
};

const dropdownStyle = css`
  padding: 0;

  .tippy-content {
    padding: 0;
  }
`;

const SessionNotRecordedPopoverContent = ({
  lowTrafficCoverage,
  currentSite,
  coveragePercentage,
}: {
  lowTrafficCoverage: boolean;
  currentSite: Site | undefined;
  coveragePercentage: number;
}) => {
  const { t } = useTranslation('feedback');
  const helpHotjarUrl = useHelpHotjarUrl();

  return (
    <div>
      <S.DropdownBody>
        <S.DropdownTitle lowTrafficCoverage={lowTrafficCoverage}>
          {t('session-not-recorded-popover.title')}
        </S.DropdownTitle>
        <S.DropdownText lowTrafficCoverage={lowTrafficCoverage}>
          {lowTrafficCoverage
            ? t('session-not-recorded-popover.sub-title--low-coverage', {
                coveragePercentage,
              })
            : t('session-not-recorded-popover.sub-title')}
        </S.DropdownText>
        {lowTrafficCoverage && (
          <S.DropdownSubText>
            {t('session-not-recorded-popover.low-traffic.sub-title2')}{' '}
            <Link
              as="a"
              href={`${helpHotjarUrl}${LEARN_MORE_EXTERNAL_LINK}`}
              kind="inline"
              isExternal
            >
              {t('session-not-recorded-popover.low-traffic.learn-more-link')}
            </Link>
          </S.DropdownSubText>
        )}
        {!lowTrafficCoverage && (
          <Button
            as="a"
            href={`${helpHotjarUrl}${LEARN_MORE_EXTERNAL_LINK}`}
            kind="secondary"
            size="s"
            isExternal
          >
            {t('session-not-recorded-popover.learn-more-cta')}
          </Button>
        )}
      </S.DropdownBody>
      {lowTrafficCoverage && (
        <S.Footer>
          <S.FooterLabel>
            {t('session-not-recorded-popover.low-traffic.footer.label')}
          </S.FooterLabel>
          <Button
            data-testid="session-not-recorded-low-traffic-upsell-cta"
            icon="crown"
            kind="primary"
            size="s"
            onClick={() =>
              currentSite &&
              upsell({
                site: currentSite,
                referrer: 'sampling-widget.tracking-coverage-message.sumup.available-plans',
              }).then(noop, noop)
            }
          >
            {t('session-not-recorded-popover.low-traffic.footer.upsell-cta')}
          </Button>
        </S.Footer>
      )}
    </div>
  );
};

export const SessionNotRecordedPopover = ({
  children,
  onShow,
}: SessionNotRecordedPopoverProps): JSX.Element => {
  const { data: currentSite } = useCurrentSite();
  const { coveragePercentage } = useTrafficCoverage();

  const lowTrafficCoverage = coveragePercentage < 50;
  return (
    <Dropdown
      css={dropdownStyle}
      onShow={onShow}
      trigger={<div>{children}</div>} // ensure Dropdown trigger has an accessible ref
      triggerEvent="mouseenter focus"
      // FeedbackResponseModal's z-index is 900001 so the tooltip's z-index should be higher.
      zIndex={900002}
    >
      <SessionNotRecordedPopoverContent
        currentSite={currentSite}
        lowTrafficCoverage={lowTrafficCoverage}
        coveragePercentage={coveragePercentage}
      />
    </Dropdown>
  );
};
